<template>
  <div class="min-h-screen flex items-start justify-center bg-white relative">
    <!-- Animated Card -->
    <div class="w-full max-w-md px-8 slide-up">
      <!-- Logo -->
      <div class="flex justify-center mt-4">
        <router-link to="/">
          <img
            src="../assets/logo-primary.png"
            alt="IMF Logo"
            class="h-auto w-72 transition-transform duration-300 hover:scale-105"
          />
        </router-link>
      </div>

      <!-- Title & Subtitle -->
      <div class="relative bottom-6">
        <div class="text-center py-3">
          <span class="text-zinc-700 font-medium">Operating Hours</span>
          <p class="text-sm">We are open Monday through Sunday</p>
          <p class="text-sm">from 8:00 AM to 8:00 PM</p>
        </div>
        <h1 class="text-2xl font-extrabold text-center text-zinc-800 mb-4">
          Welcome back
        </h1>
        <p class="text-center text-gray-500 mb-6">
          Please enter your
          <span class="font-medium text-gray-700">Control Number</span> to sign
          in
        </p>

        <!-- Form -->
        <form @submit.prevent="handleSubmit">
          <div class="mb-6 relative">
            <input
              type="text"
              placeholder=" "
              v-model="controlNumber"
              class="peer w-full text-base font-medium bg-white text-gray-800 rounded-2xl px-4 pt-6 pb-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-400 shadow-sm focus:shadow-orange-300 transition duration-300"
              autocomplete="off"
              required
            />
            <label
              class="absolute left-4 top-2 text-gray-500 text-xs font-medium transition-all duration-200 peer-placeholder-shown:text-base peer-placeholder-shown:top-4 peer-focus:text-xs peer-focus:top-2"
            >
              Control Number
            </label>
          </div>

          <!-- Submit Button -->
          <button
            type="submit"
            :disabled="loading"
            class="w-full py-3 bg-primary hover:bg-hover text-white font-semibold rounded-lg transition duration-200 flex items-center justify-center"
          >
            <span v-if="!loading">Sign In</span>
            <span v-else>
              <svg
                class="animate-spin h-5 w-5 mr-2 text-white inline-block"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                ></path>
              </svg>
              Signing in...
            </span>
          </button>

          <!-- Validation Alert -->
          <div
            v-if="validationAlert"
            class="mb-4 mt-6 flex items-center justify-center"
          >
            <div
              class="inline-block px-4 py-2 rounded-full bg-red-50 text-red-700 text-sm font-semibold shadow-sm border border-red-200"
            >
              {{ validationAlert }}
            </div>
          </div>
        </form>

        <!-- Footer Links -->
        <div class="flex flex-col items-center gap-6">
          <div class="mt-4 text-center text-sm text-gray-600">
            <router-link to="/portal/reset-account" class="cursor-pointer">
              Forgot Control Number?
            </router-link>
          </div>

          <div class="flex items-center w-full max-w-xs gap-2">
            <hr class="flex-grow border-t border-gray-400" />
            <span class="text-black text-xs uppercase">or</span>
            <hr class="flex-grow border-t border-gray-400" />
          </div>

          <div class="text-center text-sm text-gray-600">
            Not yet registered?
            <router-link
              to="/portal/registration"
              class="text-blue-500 hover:underline"
            >
              Click here
            </router-link>
          </div>
        </div>
      </div>
      <div class="text-center text-xs text-zinc-500">{{ appVersion }}</div>
    </div>
  </div>
</template>

<script>
import { ref, inject } from "vue";
import { useRouter } from "vue-router";
import systemVersion from "@/utils/version";
export default {
  setup() {
    const axios = inject("$axios");
    const store = inject("$store");
    const controlNumber = ref("");
    const validationAlert = ref("");
    const loading = ref(false);
    const router = useRouter();
    const { appVersion } = systemVersion();

    const handleSubmit = async () => {
      loading.value = true;

      // Add 3-second delay before sending the request
      await new Promise((resolve) => setTimeout(resolve, 2000));

      try {
        const response = await axios.post("/api/patient/signin", {
          controlNumber: controlNumber.value,
        });
        if (response.data?.token) {
          localStorage.setItem(
            process.env.VUE_APP_AUTH_TOKEN,
            response.data.token,
          );

          store.commit("setUserLogged", response.data);
          await getProfileAvatar();

          router.push("/portal/");
        } else {
          validationAlert.value = response.data.message;

          // Clear alert after 6 seconds
          setTimeout(() => {
            validationAlert.value = "";
          }, 6000);
        }
      } catch (error) {
        console.error("Sign-in error:", error);
        alert("Something went wrong. Please try again.");
      } finally {
        loading.value = false;
      }
    };
    const getProfileAvatar = async () => {
      try {
        const response = await axios.get(`/api/portal/patient/profile`, {
          params: {
            filename: store.state.userProfilePhoto,
          },
        });

        let profileImage = response.request.responseURL;
        localStorage.setItem("patientProfile", profileImage);
      } catch (error) {
        console.error("Failed to fetch profile image:", error);
      }
    };

    return {
      controlNumber,
      loading,
      validationAlert,
      handleSubmit,
      getProfileAvatar,
      appVersion,
    };
  },
};
</script>
<style scoped>
@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.slide-up {
  animation: slideUp 0.6s ease-out forwards;
}
</style>
